// <variant-picker> (§7.4): opciones como <fieldset> con radios de 48 px. Al cambiar actualiza la URL (?variant=),
// la disponibilidad de cada opción y el precio, y avisa al resto de componentes con «variant:change».
// Sin preselección: la talla se elige a conciencia (salvo que la URL traiga ?variant=).
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('[data-section="main-product"]');
    this.product = JSON.parse(this.section.querySelector('[data-product-json]').textContent);
    this.fieldsets = [...this.querySelectorAll('.product-option')];
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-value]')) this.update(true);
    });
    document.addEventListener('size-finder:choose', (event) => {
      if (event.detail.productHandle === this.product.handle) this.choose(event.detail);
    });
    const fromUrl = new URL(location.href).searchParams.get('variant');
    const variant = this.product.variants.find((v) => String(v.id) === fromUrl);
    if (variant) this.setValues(variant.options);
    this.update(false);
  }

  values() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
  }

  setValues(values) {
    this.fieldsets.forEach((fs, i) => {
      const input = [...fs.querySelectorAll('input')].find((el) => el.value === values[i]);
      if (input) input.checked = true;
    });
  }

  variant() {
    const values = this.values();
    if (values.some((v) => v == null)) return null;
    return this.product.variants.find((v) => v.options.every((o, i) => o === values[i])) ?? null;
  }

  update(fromUser) {
    const values = this.values();
    this.fieldsets.forEach((fs, index) => {
      fs.querySelectorAll('input[data-option-value]').forEach((input) => {
        const candidate = values.map((v, i) => (i === index ? input.value : v));
        const available = this.product.variants.some((variant) => variant.available && candidate.every((v, i) => v == null || variant.options[i] === v));
        const label = input.nextElementSibling;
        input.disabled = !available && !input.checked;
        label.textContent = available ? input.value : `${input.value} · ${this.product.strings.unavailableSuffix}`;
      });
    });
    const variant = this.variant();
    if (variant && fromUser) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url);
      window.Shopify?.analytics?.publish?.('pawly:select_variant', { variant_id: variant.id, size: variant.options[0], rear_piece: variant.options[1] ?? null });
    }
    if (variant) this.querySelector('.product__variant-error')?.setAttribute('hidden', '');
    // Precio de la variante elegida (ya formateado por la tienda) en el bloque de precio y en la barra fija.
    if (variant?.price_formatted) {
      this.section.querySelectorAll('[data-price]').forEach((price) => {
        price.textContent = variant.price_formatted;
      });
    }
    // El estado queda también en la sección: quien se conecte después (los módulos pueden cargar en otro
    // orden) lo lee al arrancar y no se pierde la variante que trae la URL.
    const detail = { productHandle: this.product.handle, variant, values, complete: values.every(Boolean) };
    this.section.variantState = detail;
    document.dispatchEvent(new CustomEvent('variant:change', { detail }));
  }

  choose({ size, sex }) {
    const rear = sex === 'macho' ? 'Macho' : sex === 'hembra' ? 'Hembra' : null;
    this.setValues(this.product.options.map((name) => (name === 'Talla' ? size : name === 'Pieza trasera' ? rear : null)));
    this.update(true);
    this.fieldsets[0]?.querySelector('input:checked')?.focus();
  }

  /** Lleva el foco al primer grupo sin elegir y muestra el error (lo usa el botón de compra). */
  showMissing(message) {
    const error = this.querySelector('.product__variant-error');
    error.querySelector('span').textContent = message;
    error.removeAttribute('hidden');
    const missing = this.fieldsets.find((fs) => !fs.querySelector('input:checked'));
    (missing?.querySelector('input:not(:disabled)') ?? this.fieldsets[0]?.querySelector('input'))?.focus();
  }
}

customElements.define('variant-picker', VariantPicker);
